import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, FormControlLabel, RadioGroup, Radio, Typography } from '@mui/material';
import { InputField } from '../../../components/InputController';
import { useFormik } from 'formik';
import { ApplicationQuestionAndAnswerDto, CustomQuestionTypeEnum, CustomQuestionResponseTypeEnum, UpsertApplicationPetV2Dto, ApplicationQuestionResponseV2Dto } from '@inosio/payments-sdk';

import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { CustomDropZone } from '../../../pmui/onboarding/components/CustomDropZone';
import { uploadDocuments, selectDocumentState, deleteDocument } from '../../../shared/sagas/document/documentSlice';
import { applicationStyles } from './CustomStyles';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { useEffect, useState } from 'react';
import { applicantDocumentMetadata, getApplicantPetRecord, selectApplicantsState, updateApplicantPetRecord, updateCustomQuestionsAnswersV2 } from '../sagas/applicants/applicantsSlice';
import { useParams } from 'react-router';
import * as Yup from 'yup';
import CustomQuestionsSection from './CustomQuestionsSection';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';

interface ApplicationPetPropsType {}

const schemaValidationApplicationPet = Yup.object({
  Species: Yup.string().max(45, 'Max length is 45 characters').required('Please enter the species of your pet'),
  Name: Yup.string().max(45, 'Max length is 45 characters').required('Please enter the name of your pet'),
  Breed: Yup.string().max(45, 'Max length is 45 characters').required('Please enter the breed of your pet'),
  WeightLbs: Yup.string().min(0).required('Please enter the weight of your pet'),
  LivesOnPremises: Yup.boolean().required(),
});

type schemaValidationApplicationPetType = Yup.InferType<typeof schemaValidationApplicationPet>;

export default function ApplicationPet(props: ApplicationPetPropsType) {
  const query = useQuery();
  const hasValidationQueryParam = !!query.get('validation');
  const { id: petUuidParam } = useParams<{ id: string }>();
  const classes = applicationStyles();
  const dispatch = useDispatch();

  //selectors
  const { ipaUser } = useSelector(selectAuthState);
  const {
    isFetchingApplicantPetRecordSuccess,
    isFetchingApplicantPetRecordFailed,
    applicantPetRecord,
    Application,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    CustomQuestionAnswers,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    fetchDocumentResponse,
    isFetchingDocumentsSuccess,
    isFetchingDocumentsFailed,
  } = useSelector(selectApplicantsState);
  const { isUploadingDocuments, isUploadingDocumentsSuccess, isUploadingDocumentsFailed } = useSelector(selectDocumentState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);

  //local states
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<Array<ApplicationQuestionAndAnswerDto>>([]);
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [currentPetUuidParam] = useState(petUuidParam);
  const [initialFormValues, setInitialFormValues] = useState<schemaValidationApplicationPetType>({
    Species: '',
    Name: '',
    Breed: '',
    WeightLbs: '',
    LivesOnPremises: true,
  });
  const [petFiles, setPetFiles] = React.useState<Array<any>>([]);
  const requiresPetFilesUpload = !!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsRequirePetsDocsUpload;

  //fetch pet records
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid && isFetchingApplicantPetRecordSuccess) {
      setInitialFormValues({
        ...initialFormValues,
        Species: applicantPetRecord.Species ?? '',
        Name: applicantPetRecord.Name ?? '',
        Breed: applicantPetRecord.Breed ?? '',
        WeightLbs: applicantPetRecord.WeightLbs ? `${applicantPetRecord.WeightLbs}` : '',
        LivesOnPremises: applicantPetRecord.LivesOnPremises ?? true,
      });
    }
  }, [ipaUser, currentPetUuidParam, isFetchingApplicantPetRecordSuccess]);

  const handleFormValuesChanged = (e: any) => {
    /*
     */

    const payload = {
      Name: !formik.errors.Name ? formik.values.Name || undefined : undefined,
      Species: !formik.errors.Species ? formik.values.Species || undefined : undefined,
      Breed: !formik.errors.Breed ? formik.values.Breed || undefined : undefined,
      WeightLbs: !formik.errors.WeightLbs ? parseInt(formik.values.WeightLbs || '0') || undefined : undefined,
      LivesOnPremises: !formik.errors.LivesOnPremises ? formik.values.LivesOnPremises : undefined,
    } as UpsertApplicationPetV2Dto;

    for (const [key] of Object.entries(payload)) {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    }

    dispatch({
      type: updateApplicantPetRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        petUuid: currentPetUuidParam,
        updateDto: payload,
      },
    });
  };

  const handleSaveCustomQuestionAnswer = (e: any, questionUuid: string) => {
    const answeredQuestion = questionsAndAnswers.find((q) => q.Uuid === questionUuid);

    if (answeredQuestion) {
      dispatch({
        type: updateCustomQuestionsAnswersV2.type,
        payload: {
          applicationUuid: Application.Uuid,
          upsertDto: {
            QuestionUuid: answeredQuestion?.Uuid,
            ResponseType: answeredQuestion?.CustomQuestionResponseType,
            Response: answeredQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean ? e.target.value === 'true' : e.target.value,
            BelongsToUuid: currentPetUuidParam,
            ApplicantUuid: ipaUser?.currentProfile.uuid,
          } as ApplicationQuestionResponseV2Dto,
        },
      });
    }
  };

  const handleCustomQuestionFieldChanged = (e: any, questionUuid: string) => {
    setQuestionsAndAnswers(
      questionsAndAnswers.map((qa) => {
        if (qa.Uuid === questionUuid) {
          //found our question.

          if (qa.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
            return {
              ...qa,
              BooleanQuestionAnswer: e.target.value === 'true',
            };
          } else
            return {
              ...qa,
              TextQuestionAnswer: e.target.value,
            };
        }
        return qa;
      }),
    );
  };

  const formik = useFormik<schemaValidationApplicationPetType>({
    initialValues: initialFormValues,
    validationSchema: schemaValidationApplicationPet,
    onSubmit: async (values) => {},
    enableReinitialize: true,
    validateOnMount: true,
  });

  const handlePetFilesAdded = (filesToUpload: File[]) => {
    let files = petFiles;

    //check if current file count + newly dropped files is eq or less than 3
    if (files.length + filesToUpload.length <= 3) {
      filesToUpload.forEach((af: File) => {
        //for each new file dropped, only allow it that same file name doesn't already exist in our set
        if (!files.find((f) => f.name === af.name)) {
          files = [...files, af];
        }
      });
      setPetFiles(files);

      const uploadingFiles = files?.filter((f) => f.name && !f.remoteUuid);
      if (uploadingFiles.length > 0) {
        dispatch({
          type: uploadDocuments.type,
          payload: {
            belongsToEntityUuid: currentPetUuidParam,
            documents: uploadingFiles,
            documentType: 'PetPhoto',
          },
        });
      }
    }
  };

  const handlePetFilesDelete = (file: any) => {
    if (file.remoteUuid) {
      dispatch(deleteDocument(file.remoteUuid));
    } else {
      //there is no remoteUuid, so it must not have even uploaded. Just remove it from local state.
      const newList = petFiles.filter((f) => f.name !== file.name);
      setPetFiles(newList);
    }
  };

  //deal with the documents once received
  useEffect(() => {
    if (isFetchingDocumentsSuccess) {
      const filesForThisSpecificRecord = fetchDocumentResponse
        ?.filter((d) => d.documentTypeId === 5 && d.belongsToEntityUuid === currentPetUuidParam)
        .map((d) => {
          const file: applicantDocumentMetadata = {
            name: d.fileName, //is just this good enough?
            remoteUuid: d.remoteUuid,
          };
          return file;
        });
      setPetFiles(filesForThisSpecificRecord);
    }
  }, [isFetchingDocumentsSuccess, fetchDocumentResponse]);

  //populate custom questions once prop group details received
  useEffect(() => {
    if (propertyGroupDetails?.Uuid && isFetchingCustomQuestionAnswersSuccess) {
      const organizationPetsCustomQuestions = (propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsQuestions ?? []).filter(
        (q) => q.CustomQuestionType === CustomQuestionTypeEnum.PetV1,
      );
      setQuestionsAndAnswers(
        organizationPetsCustomQuestions.map((q) => {
          const previouslySavedResponseToThisQuestion = CustomQuestionAnswers?.find((a) => a.QuestionUuid === q.Uuid && a.BelongsToUuid === currentPetUuidParam);

          const question: ApplicationQuestionAndAnswerDto = {
            ...q,
            ApplicantUuid: previouslySavedResponseToThisQuestion?.ApplicantUuid!,
            BelongsToEntityUuid: currentPetUuidParam,
          };

          switch (question.CustomQuestionResponseType) {
            case CustomQuestionResponseTypeEnum.FreeText:
              question.TextQuestionAnswer = previouslySavedResponseToThisQuestion?.Response?.toString();
              break;
            case CustomQuestionResponseTypeEnum.Boolean:
              question.BooleanQuestionAnswer = previouslySavedResponseToThisQuestion?.Response === undefined ? undefined : Boolean(previouslySavedResponseToThisQuestion?.Response);
              break;
            default:
              break;
          }

          return question;
        }),
      );
    }
  }, [propertyGroupDetails, isFetchingCustomQuestionAnswersSuccess]);

  //fetch the pet record on load
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid && currentPetUuidParam) {
      dispatch({
        type: getApplicantPetRecord.type,
        payload: {
          applicantUuid: ipaUser.currentProfile.uuid,
          petUuid: currentPetUuidParam,
        },
      });
    }
  }, [dispatch, ipaUser, currentPetUuidParam]);

  //error handling
  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingApplicantPetRecordSuccess && isFetchingApplicationSuccess && isFetchingCustomQuestionAnswersSuccess && isFetchingDocumentsSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingApplicantPetRecordFailed || isFetchingApplicationFailure || isFetchingCustomQuestionAnswersFailure || isFetchingDocumentsFailed) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the pet information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [
    isFetchingPropertyGroupDetailsSuccess,
    isFetchingPropertyGroupDetailsFailed,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    isFetchingDocumentsSuccess,
    isFetchingDocumentsFailed,
    isFetchingApplicantPetRecordSuccess,
  ]);

  //show/hide error if document upload failed
  useEffect(() => {
    if (isUploadingDocumentsFailed) {
      setLocalError('Error uploading document(s).');
    }
  }, [isUploadingDocumentsSuccess, isUploadingDocumentsFailed]);

  return (
    <Box sx={{ width: '100%', minHeight: '60vh' }}>
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box sx={{ width: '100%', p: 4, minHeight: 'inherit' }}>
          {localError && <Typography className={classes.globalText}>{localError}</Typography>}
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} lg={4.5} xl={3.5}>
              <Typography sx={{ mb: 2 }} className={classes.labelText}>
                Animal Details
              </Typography>
              <InputField
                label={'Species'}
                isError={!!formik.errors?.Species && hasValidationQueryParam}
                errors={formik.errors?.Species}
                touched={formik.touched?.Species}
                {...formik.getFieldProps('Species')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showAsterisk={!formik.values.Species}
              />
              <InputField
                label={'Name'}
                isError={!!formik.errors?.Name && hasValidationQueryParam}
                errors={formik.errors?.Name}
                touched={formik.touched?.Name}
                {...formik.getFieldProps('Name')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showAsterisk={!formik.values.Name}
              />
              <InputField
                label={'Breed'}
                isError={!!formik.errors?.Breed && hasValidationQueryParam}
                errors={formik.errors?.Breed}
                touched={formik.touched?.Breed}
                {...formik.getFieldProps('Breed')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                showAsterisk={!formik.values.Breed}
              />
              <InputField
                label={'Weight (lbs)'}
                isError={!!formik.errors?.WeightLbs && hasValidationQueryParam}
                errors={formik.errors?.WeightLbs}
                touched={formik.touched?.WeightLbs}
                {...formik.getFieldProps('WeightLbs')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                onChange={(e) => {
                  const input = e.target.value;
                  const newValue = input.replace(/[^0-9]/g, ''); // Remove non-numeric characters
                  formik.setFieldValue('WeightLbs', newValue);
                }}
                showAsterisk={!formik.values.WeightLbs}
              />
              <Typography sx={{ mt: 1, mb: 0.5 }} className={classes.labelText}>
                Does the animal reside in or enter the premises? <span style={{ color: 'red' }}>*</span>
              </Typography>
              <RadioGroup
                row
                aria-labelledby="premises-label"
                value={formik.values.LivesOnPremises}
                onChange={(event) => {
                  formik.setFieldValue('LivesOnPremises', event.target.value === 'true');
                  formik.setFieldTouched('LivesOnPremises', true);
                }}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                name="LivesOnPremises"
              >
                <FormControlLabel value={false} control={<Radio />} sx={formik.values.LivesOnPremises == null && hasValidationQueryParam ? { color: 'red' } : {}} label="No" />
                <FormControlLabel value={true} control={<Radio />} sx={formik.values.LivesOnPremises == null && hasValidationQueryParam ? { color: 'red' } : {}} label="Yes" />
              </RadioGroup>
              {!!currentPetUuidParam && (
                <Grid item xs={12}>
                  <Typography sx={{ mt: 1, mb: 2 }} className={classes.labelText}>
                    Upload animal photos:
                  </Typography>
                  {/* maxFileSize must be in MB, example: maxFileSize={20} */}
                  <CustomDropZone
                    files={petFiles}
                    fileLimit={3}
                    handleFilesAdded={handlePetFilesAdded}
                    handleFileDeleted={handlePetFilesDelete}
                    description="Up to 20MB per file. Up to 3 Files at a time."
                    maxFileSize={20}
                    height="250px"
                    fileTypes={['image/png', 'image/jpeg', 'application/pdf']} //.txt
                    validationError={hasValidationQueryParam && !petFiles.length && requiresPetFilesUpload} // User is in validation mode and they haven't uploaded Pet Docs even though the PM requires it.
                    startProgress={isUploadingDocuments}
                    stopProgress={isUploadingDocumentsSuccess}
                    syncProgress={true}
                  />
                  <Typography style={{ fontFamily: 'Work Sans', fontSize: '14px', marginTop:'10px' }}>{'Supported File Types: .jpg, .pdf, .png'}</Typography>
                </Grid>
              )}
            </Grid>
            <CustomQuestionsSection
              questionsAndAnswers={questionsAndAnswers}
              hasValidationQueryParam={hasValidationQueryParam}
              onFieldChange={handleCustomQuestionFieldChanged}
              onFieldUpdate={handleSaveCustomQuestionAnswer}
            />
          </Grid>
        </Box>
      )}
    </Box>
  );
}
